/**
 * Locale-aware formatter HOOKS.
 *
 * These are the React-tree counterpart to `localeFormat.ts`: they resolve the
 * active language through `react-i18next` so a component re-renders when the
 * locale changes mid-session, and they build their `Intl` instances from the
 * SAME cache and the SAME pinned rounding policy as the non-hook helpers.
 *
 * That shared construction is the point. When the hooks owned their own
 * `new Intl.NumberFormat(...)` calls, a table cell rendered through
 * `useNumberFormat()` and the CSV export rendered through a module-scope
 * helper could disagree about the same value — different precision, and
 * (before the policy was pinned) different midpoint rounding. Both paths now
 * terminate in `numberFormatter` / `dateFormatter`.
 *
 * Prefer the plain `format*` functions from `localeFormat.ts` in `lib/` logic
 * and module-scope helpers, which cannot run a hook. Use these when a
 * component must react to an in-session language switch.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveActiveLocale } from './useActiveLocale.ts';
import {
  dateFormatter,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercentOfFraction,
  formatPercentOfPercent,
  formatQuantity,
  numberFormatter,
  type DateFormatOptions,
  type NumberFormatOptions,
} from './localeFormat.ts';

/**
 * The active locale for the calling component. Prefers react-i18next's current
 * language (so `changeLanguage` re-renders consumers) and falls back to the
 * registered tenant/user resolver — never to a hard-coded literal.
 */
function useResolvedLocale(): string {
  const { i18n } = useTranslation();
  return i18n?.language || resolveActiveLocale();
}

/** Number formatter for the active locale, with the product rounding policy. */
export function useNumberFormat(options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const locale = useResolvedLocale();
  return useMemo(() => numberFormatter(locale, options), [locale, options]);
}

/** Currency formatter — pass the ISO-4217 code (e.g. 'USD', 'EUR'). */
export function useCurrencyFormat(
  currency: string,
  options?: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const locale = useResolvedLocale();
  return useMemo(
    () => numberFormatter(locale, { style: 'currency', currency, ...options }),
    [locale, currency, options],
  );
}

/** Date/time formatter for the active locale. */
export function useDateFormat(options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = useResolvedLocale();
  return useMemo(() => dateFormatter(locale, options), [locale, options]);
}

/**
 * The whole named-formatter set, bound to the active locale.
 *
 * This is the hook most views want: one object whose members are the same
 * functions `localeFormat.ts` exports, with `locale` already applied, so a view
 * never names a locale and never picks a precision by typing a digit count.
 *
 * ```tsx
 * const f = useLocaleFormat();
 * <td>{f.quantity(row.netMass, row.uom)}</td>
 * <td>{f.percentOfFraction(row.yield)}</td>
 * <td>{f.dateTime(row.releasedAt)}</td>
 * ```
 */
export interface LocaleFormatters {
  /** The locale every member of this object formats against. */
  locale: string;
  number: (value: unknown, options?: NumberFormatOptions) => string;
  count: (value: unknown, options?: NumberFormatOptions) => string;
  currency: (
    value: unknown,
    currency: string | null | undefined,
    options?: NumberFormatOptions,
  ) => string;
  percentOfFraction: (value: unknown, options?: NumberFormatOptions) => string;
  percentOfPercent: (value: unknown, options?: NumberFormatOptions) => string;
  quantity: (
    value: unknown,
    uom: string | null | undefined,
    options?: NumberFormatOptions,
  ) => string;
  date: (value: Date | number | string | null | undefined, options?: DateFormatOptions) => string;
  dateTime: (
    value: Date | number | string | null | undefined,
    options?: DateFormatOptions,
  ) => string;
}

export function useLocaleFormat(): LocaleFormatters {
  const locale = useResolvedLocale();
  return useMemo<LocaleFormatters>(
    () => ({
      locale,
      number: (v, o) => formatNumber(v, { locale, ...o }),
      count: (v, o) => formatNumber(v, { locale, decimals: 'count', ...o }),
      currency: (v, c, o) => formatCurrency(v, c, { locale, ...o }),
      percentOfFraction: (v, o) => formatPercentOfFraction(v, { locale, ...o }),
      percentOfPercent: (v, o) => formatPercentOfPercent(v, { locale, ...o }),
      quantity: (v, u, o) => formatQuantity(v, u, { locale, ...o }),
      date: (v, o) => formatDate(v, { locale, ...o }),
      dateTime: (v, o) => formatDateTime(v, { locale, ...o }),
    }),
    [locale],
  );
}
